/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Inject, Input, OnInit, signal} from '@angular/core';
import {notifySignalView} from '../../common/util/template-state';
import * as _ from 'lodash';

import template from './site-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {normalizeTargetingSearchPage} from '../normalize-targeting-search-page';
import {TargetingTemplateHook} from './targeting-template.component';
import {findCountryName} from '../../common/directives/country-select/countries';
import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {convertIabCategoryToString} from '../../../components/util/iab-taxonomy';
import {SiteApiService} from '../../site/site-api.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {SearchApiService} from '../../common/controller/search-api.service';

@Component({
  standalone: false,
  selector: 'adn-site-targeting',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteTargetingComponent implements OnInit {
  @Input() model: any;
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.site;
  @Input() underModel: any;
  @Input() modelLineItem: any;
  @Input() runningModel: any;
  @Input() mandatoryTargets: any;
  @Input() lineItemType: string;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  searchTerm = '';
  list: any = {searchResults: []};
  filter: any = {};
  limited = false;
  limitedType = '';
  isPureMarketplace = false;
  showMerchantColumn = false;
  merchantsById: any = {};
  merchantFilterBucketList: any[] = [];
  countryBuckets: any[] = [];
  categoryBuckets: any[] = [];
  hasCountries = false;
  hasCategories = false;
  hasCountriesTable = false;
  hasMerchantFilter = false;
  tabActivated = false;
  readonly viewRevision = signal(0);

  searchMeta: any = {
    pageSize: 50,
    currentPage: 1,
    resolvedError: '',
    filterResolvedError: '',
    filterResolved: true,
    resolved: true,
    changePage: () => this.search(false, true)
  };

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      const model = this.model || {};
      const summary = (model.negated === true ? 'NOT: ' : '') + _.map(model.sites, 'name').join(', ');
      return {
        vm: _.cloneDeep(model),
        model: _.cloneDeep(model),
        constant: ADN_TARGETING_TYPES.site,
        summary
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  constructor(
    private readonly siteApi: SiteApiService,
    private readonly searchApi: SearchApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
  }

  ngOnInit(): void {
    this.ensureModel();
    this.isPureMarketplace = this.LocalNetworkProfile.isPureMarketplacePlatform();
    this.initMerchants();

    const countriesSelected = _.get(
      this.runningModel,
      [ADN_TARGETING_TYPES.siteCountry.targets, ADN_TARGETING_TYPES.siteCountry.apiProp]
    );
    if (_.isArray(countriesSelected) && countriesSelected.length === 1) {
      this.filter = this.filter || {};
      this.filter.country = countriesSelected[0].toLowerCase();
    }

    if (!this.tabController) {
      return;
    }

    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.site.widget, () => this.onTabSelect(), copyTemplate);
  }

  merchantSummaryForSite(site: any): string {
    if (!site) {
      return '';
    }
    const mids = site.retailMerchantIds;
    const arr = _.isArray(mids) ? _.uniq(_.filter(_.map(mids, (mid: any) => {
      return _.isString(mid) ? parseInt(mid, 10) : mid;
    }), _.isFinite)) : [];
    if (!arr.length) {
      return 'All';
    }
    return _.map(arr, (id: any) => this.merchantLabelFromId(id)).join(', ');
  }

  selectAll(): void {
    _.forEach(_.get(this.list, ['searchResults'], []), (site: any) => {
      site.selected = true;
      this.model.sites.push(site);
    });
    this.model.sites = _.uniqBy(this.model.sites, 'id');
  }

  removeAll(): void {
    this.model.sites = [];
    if (this.list && this.list.searchResults) {
      _.forEach(this.list.searchResults, (site: any) => {
        site.selected = false;
      });
    }
  }

  select(site: any): void {
    site.selected = true;
    this.model.sites.push(site);
  }

  deselect(site: any): void {
    _.remove(this.model.sites, ['id', site.id]);
    if (this.list && this.list.searchResults) {
      (_.find(this.list.searchResults, ['id', site.id]) || {}).selected = false;
    }
  }

  search(newSearch?: boolean, filterSearch?: boolean): void {
    const metaResolvedParam = filterSearch ? 'filterResolved' : 'resolved';
    const metaResolvedErrorParam = metaResolvedParam + 'Error';
    this.searchMeta[metaResolvedParam] = false;
    this.searchMeta[metaResolvedErrorParam] = false;

    if (!filterSearch) {
      this.filter = {
        country: _.get(this.filter, ['country']) || null,
        category: null,
        retailMerchantIds: _.get(this.filter, ['retailMerchantIds']) || null
      };
    }

    if (newSearch) {
      this.searchMeta.currentPage = 1;
    }

    const searchParams: any = {
      q: this.searchTerm || '',
      types: 'Site',
      objectState: 'ACTIVE',
      pageSize: this.searchMeta.pageSize,
      page: this.searchMeta.currentPage - 1
    };
    if (_.get(this.modelLineItem, ['axProductIsHighImpact'])) {
      searchParams.isFullPage = true;
    }
    if (this.filter.country) {
      searchParams.country = this.filter.country;
    }
    if (this.lineItemType === 'POLITICAL') {
      searchParams.politicalAdsBlocked = false;
    }
    if (this.filter.category) {
      searchParams.categories = this.filter.category;
    }
    if (this.filter.retailMerchantIds) {
      searchParams.retailMerchantIds = this.filter.retailMerchantIds;
    }
    const orderId = _.get(this.modelLineItem, ['order', 'id']);
    if (orderId) {
      searchParams.filterBy = 'ORDER_TEAM';
      searchParams.filterByValue = orderId;
    }

    const targeting = this.runningModel || {};
    if (targeting.publisherTarget && targeting.publisherTarget.earningsAccounts && targeting.publisherTarget.negated === false) {
      searchParams.earningsAccount = _.map(targeting.publisherTarget.earningsAccounts, (s: any) => {
        return s.id || s;
      });
    }
    if (targeting.siteGroupTarget && targeting.siteGroupTarget.siteGroups && targeting.siteGroupTarget.negated === false) {
      searchParams.siteGroup = _.map(targeting.siteGroupTarget.siteGroups, (s: any) => {
        return s.id || s;
      });
    }

    this.searchApi.search(searchParams).subscribe({
      next: (page) => {
        const limited = (searchParams.earningsAccount && searchParams.earningsAccount.length > 0) ||
          (searchParams.siteGroup && searchParams.siteGroup.length > 0);
        const limitedType = this.isPureMarketplace ? 'publisher' : 'earnings account';
        const normalizedPage = normalizeTargetingSearchPage(page, {
          pageSize: this.searchMeta.pageSize,
          currentPage: this.searchMeta.currentPage,
          mapItem: (item: any) => {
            item.categories = _.map(item.categories, convertIabCategoryToString);
            item.country = item.country ? findCountryName(item.country) : '';
            return item;
          }
        });

        const categoryBuckets = this.normalizeBuckets(_.get(page, ['aggregations', 'categories', 'buckets']));
        const countryBuckets = this.normalizeBuckets(_.get(page, ['aggregations', 'country', 'buckets']));
        const retailMerchantBuckets = this.normalizeBuckets(_.get(page, ['aggregations', 'retailMerchantIds', 'buckets']));
        const isMandatory = (this.mandatoryTargets || []).indexOf(ADN_TARGETING_TYPES.siteCountry.id) > -1;
        const hasCountries = this.localNetworkInfo.getSiteCountries().length > 1 &&
          !isMandatory && (this.filter.country || !_.isEmpty(countryBuckets));
        const hasCategories = this.filter.category || !_.isEmpty(categoryBuckets);
        const hasCountriesTable = this.localNetworkInfo.getSiteCountries().length > 1 &&
          (this.filter.country || !_.isEmpty(countryBuckets));

        let merchantFilterBucketList = [];
        if (this.showMerchantColumn && retailMerchantBuckets) {
          merchantFilterBucketList = _.chain(retailMerchantBuckets)
            .filter((bucket: any) => {
              return bucket && (bucket.filter !== null || bucket.name !== null);
            })
            .map((bucket: any) => {
              const filt = bucket.filter !== undefined && bucket.filter !== null ? bucket.filter : bucket.name;
              const id = parseInt(String(filt), 10);
              const merchant = this.merchantsById[id];
              const label = (merchant && (merchant.uiTitle || merchant.title)) || String(filt);
              return {filter: String(filt), label: label, count: bucket.count};
            })
            .sortBy('label')
            .value();
        }
        const hasMerchantFilter = this.showMerchantColumn &&
          (!!this.filter.retailMerchantIds || merchantFilterBucketList.length > 0);

        if (hasCountries) {
          _.forEach(countryBuckets, (country: any) => {
            country.name = findCountryName(country.name);
          });
        }
        if (hasCategories) {
          _.forEach(categoryBuckets, (category: any) => {
            category.name = convertIabCategoryToString(category.name);
          });
        }

        this.limited = limited;
        this.limitedType = limitedType;
        this.list = normalizedPage;
        this.categoryBuckets = categoryBuckets;
        this.countryBuckets = countryBuckets;
        this.hasCountries = hasCountries;
        this.hasCategories = hasCategories;
        this.hasCountriesTable = hasCountriesTable;
        this.merchantFilterBucketList = merchantFilterBucketList;
        this.hasMerchantFilter = hasMerchantFilter;
        this.updateSelectedSearchResults();
        this.searchMeta[metaResolvedParam] = true;
        notifySignalView(this.viewRevision);
      },
      error: (err: any) => {
        this.list = {};
        this.searchMeta[metaResolvedParam] = true;
        if (_.get(err, ['data', 'code']) === 'error.api.order.no.team') {
          this.searchMeta[metaResolvedErrorParam] = 'Specify a team on the order to obtain site results here';
        } else {
          const statusText = _.get(err, ['statusText']);
          if (statusText) {
            this.searchMeta[metaResolvedErrorParam] = statusText;
          }
        }
        notifySignalView(this.viewRevision);
      }
    });
  }

  changePage(page: number): void {
    if (page < 1) {
      return;
    }
    this.searchMeta.currentPage = page;
    this.search(false, true);
  }

  private onTabSelect(): void {
    if (this.tabActivated) {
      return;
    }
    this.tabActivated = true;
    this.search();
    this.hydrateSites();
  }

  private hydrateSites(): void {
    if (!this.model.sites.length) {
      return;
    }
    const ids = _.map(this.model.sites, 'id');
    const applyHydration = (page: any) => {
      const keyedResults = _.keyBy(page.results || page || [], 'id');
      this.model.sites = _.map(this.model.sites, (sel: any) => {
        const fullSiteDetail = keyedResults[sel.id];
        if (!fullSiteDetail) {
          return sel;
        }
        const hydrated = {
          ...sel,
          ..._.pick(fullSiteDetail, ['description', 'country', 'requestedInLastDay', 'creativeTypesRequestedInLastDay', 'retailMerchantIds'])
        };
        hydrated.categories = _.map(_.cloneDeep(_.get(fullSiteDetail, ['iabCategories'])), convertIabCategoryToString);
        hydrated.country = hydrated.country ? findCountryName(hydrated.country) : '';
        hydrated.earningsAccountName = _.get(fullSiteDetail, ['earningsAccount', 'name']);
        return hydrated;
      });
      notifySignalView(this.viewRevision);
    };
    this.siteApi.getMany(ids).subscribe(applyHydration);
  }

  private initMerchants(): void {
    const activeMerchants = _.filter(this.localNetworkInfo.getInfo('productFeedProductCounts') || [], (feed: any) => {
      return feed.productCount > 0;
    });
    this.showMerchantColumn = activeMerchants.length > 1;
    this.merchantsById = {};
    _.forEach(activeMerchants, (m: any) => {
      if (m && _.isFinite(m.id)) {
        this.merchantsById[m.id] = m;
        this.merchantsById[String(m.id)] = m;
      }
    });
  }

  private merchantLabelFromId(id: any): string {
    const n = _.isString(id) ? parseInt(id, 10) : id;
    if (!_.isFinite(n)) {
      return String(id);
    }
    const merchant = this.merchantsById[n];
    return (merchant && (merchant.uiTitle || merchant.title)) || String(n);
  }

  private updateSelectedSearchResults(): void {
    const selIds = _.keyBy(this.model.sites, 'id');
    _.forEach(_.get(this.list, ['searchResults'], []), (site: any) => {
      site.selected = !!selIds[site.id];
    });
  }

  private normalizeBuckets(buckets: any): any[] {
    const bucketList = _.isArray(buckets) ? buckets : (_.isObject(buckets) ? _.values(buckets) : []);
    return _.filter(bucketList, (bucket: any) => {
      return bucket && (bucket.filter !== null || bucket.name !== null) &&
        (bucket.filter !== undefined || bucket.name !== undefined);
    });
  }

  private ensureModel(): void {
    if (!this.model) {
      return;
    }
    this.model.sites = this.model.sites || [];
    if (_.isNil(this.model.negated)) {
      this.model.negated = false;
    }
  }

  private copyTemplate(tpl: any): void {
    if (!this.model) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || {sites: []};
    Object.keys(this.model).forEach((key) => delete this.model[key]);
    Object.assign(this.model, copied);
    this.ensureModel();
    this.updateSelectedSearchResults();
  }
}
